import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Badge, Box, Divider, ListItemText } from '@/shared/ui';
import { Button } from '@/shared/ui/lib/Button';
import { IconButton } from '@/shared/ui/lib/IconButton';
import { Link } from '@/shared/ui/lib/Link';
import { Loader } from '@/shared/ui/Loader';
import { Menu } from '@/shared/ui/Menu';
import { SvgIcon } from '@/shared/ui/SvgIcon';
import { CustomTypography } from '@/shared/ui/Typography';
import { dayjs } from '@/shared/utils/dayjs';
import { getRoutePath } from '@/shared/utils/routes';

import { useNotificationsList } from '../../model/useNotificationsList';
import { useUnreadNotifications } from '../../model/useUnreadNotifications';

import { StyledMenuItem } from './styles';

export const NotificationsMenu: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { notifications, isPending: isLoading } = useNotificationsList();

  const [notificationsAnchor, setNotificationsAnchor] =
    useState<HTMLElement | null>(null);
  const unreadCount = useUnreadNotifications();

  return (
    <>
      <IconButton
        size="large"
        aria-label={t('common:menu.notifications')}
        onClick={(e) => setNotificationsAnchor(e.currentTarget)}
      >
        <Badge badgeContent={unreadCount?.unread_count} color="error">
          <SvgIcon name="notifications" fontSize="small" />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={notificationsAnchor}
        open={!!notificationsAnchor}
        onClose={() => setNotificationsAnchor(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box
          sx={{
            maxHeight: 400,
            width: '100%',
            overflowY: 'auto',
          }}
        >
          {isLoading ? (
            <Loader containerSize="small" />
          ) : (
            notifications?.map((notification) => (
              <StyledMenuItem key={notification.id}>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" mb={1}>
                      <Box
                        width={8}
                        height={8}
                        borderRadius="50%"
                        bgcolor={(theme) =>
                          notification.read
                            ? theme.palette.actions?.go
                            : theme.palette.custom.black[10]
                        }
                        mr={1}
                      />
                      <CustomTypography
                        variant="caption"
                        color="text.secondary"
                      >
                        {dayjs(notification.created_at).format(
                          'DD.MM.YYYY, HH:mm'
                        )}
                      </CustomTypography>
                    </Box>
                  }
                  secondary={
                    <>
                      <CustomTypography fontWeight="bold" display="block">
                        {notification.title}
                      </CustomTypography>
                      <CustomTypography
                        variant="caption"
                        display="block"
                        gutterBottom
                      >
                        {notification.message}
                      </CustomTypography>
                      <Link href={getRoutePath('MAIN', 'NOTIFICATIONS')}>
                        {t('common:header.notifications.details')}
                      </Link>
                    </>
                  }
                  disableTypography
                />
              </StyledMenuItem>
            ))
          )}

          {notifications?.length === 0 && (
            <Box justifyContent="center" mb={1} sx={{ display: 'flex' }}>
              {t('common:header.notifications.notFound')}
            </Box>
          )}
        </Box>

        <Divider />

        <Box p={1}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              navigate({ pathname: getRoutePath('MAIN', 'NOTIFICATIONS') });
              setNotificationsAnchor(null);
            }}
          >
            {t('common:header.notifications.showAll')}
          </Button>
        </Box>
      </Menu>
    </>
  );
};
